import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../common/formkit/FormPageLayout";
import { Section, TextField, TextAreaField, CurrencyField, SegmentField } from "../../common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../common/formkit/formHelpers";
import { showToast } from "../../common/notification/Notification";
import {
  getProjectById,
  addProject,
  editProject,
  getProjectLedgersList,
  addProjectLedgerEntry,
  editProjectLedgerEntry,
  getProjectNotes,
  addProjectNote,
  editProjectNote,
} from "../../../api/tools/toolsApi";
import { PROJECTS_HOME, projectUrl, useToolsToken } from "./toolFormHelpers";
import { assertSuccess, errorText, ledgerDateInput, toLedgerApiDate, todayDate } from "./projectModel";

const PROJECT_FORM_ID = "project-form";
const LEDGER_FORM_ID = "project-ledger-form";
const NOTE_FORM_ID = "project-note-form";

// The backend requires both the project name and the client name
const projectSchema = Yup.object({
  project_name: Yup.string().trim().required("Please enter a project name"),
  client_name: Yup.string().trim().required("Please enter the client name"),
});

const ledgerSchema = Yup.object({
  type: Yup.string().oneOf(["CREDIT", "DEBIT"]).required(),
  amount: Yup.number().typeError("Please enter a valid amount").moreThan(0, "Please enter a valid amount").required("Please enter a valid amount"),
  date: Yup.string().required("Date is required"),
  remark: Yup.string(),
});

const noteSchema = Yup.object({
  title: Yup.string().trim().required("Please enter a note title"),
  note: Yup.string().trim().required("Please enter note content"),
});

const LEDGER_TYPES = [
  { value: "CREDIT", label: "Payment Received (Credit)" },
  { value: "DEBIT", label: "Expense Paid (Debit)" },
];


const NotFound = ({ text, onBack }) => (
  <div className="fk-page" style={{ maxWidth: 1200 }}>
    <div className="alert alert-warning d-flex align-items-center justify-content-between" role="alert">
      <span>{text}</span>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onBack}>
        Back to Project Ledger
      </button>
    </div>
  </div>
);

const FormBody = ({ id, saved, children }) => {
  const { dirty } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);
  return (
    <Form id={id} noValidate>
      <FocusFirstError />
      {children}
    </Form>
  );
};

/** Loads an item for an edit page: the one handed over by the list, otherwise found via `find`. */
const useEditItem = ({ enabled, handedOver, find }) => {
  const [item, setItem] = useState(handedOver || null);
  const [loading, setLoading] = useState(enabled && !handedOver);
  const [notFound, setNotFound] = useState(false);
  const token = useToolsToken();

  useEffect(() => {
    if (!enabled || item || !token) return undefined;
    let cancelled = false;
    find(token)
      .then((found) => {
        if (cancelled) return;
        if (found) setItem(found);
        else setNotFound(true);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, item, token]);

  return { item, loading, notFound };
};

/* ---------------- Create / edit a project ---------------- */

/** /partner/tools/projects/new and /partner/tools/projects/:projectId/edit */
export const ProjectFormPage = () => {
  const { projectId } = useParams();
  const isEdit = Boolean(projectId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = useToolsToken();
  const exit = useExitWizard(isEdit ? projectUrl(projectId) : PROJECTS_HOME);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { item: project, loading, notFound } = useEditItem({
    enabled: isEdit,
    handedOver: location.state?.project,
    find: async (t) => {
      const data = await getProjectById(t, projectId);
      return data?.project?.id ? data.project : null;
    },
  });

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = {
        project_name: values.project_name,
        client_name: values.client_name,
        client_mobile: values.client_mobile,
        client_address: values.address,
        description: values.description,
      };
      let targetId = projectId;
      if (isEdit) {
        assertSuccess(await editProject(token, { id: project?.id ?? projectId, ...body }), "Failed to save project");
      } else {
        const res = assertSuccess(await addProject(token, body), "Failed to save project");
        targetId = res?.project?.id;
      }
      showToast(isEdit ? "Project updated successfully" : "Project created successfully", { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(projectUrl(targetId), { replace: true }), 0);
    } catch (err) {
      showToast(errorText(err, "Failed to save project"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This project could not be found. It may have been removed." onBack={() => navigate(PROJECTS_HOME)} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Project" : "Add New Project"}
      subtitle={isEdit ? "Update the details of this project." : "Create a project to track its payments, expenses and notes."}
      formId={PROJECT_FORM_ID}
      submitLabel={isEdit ? "Update Project" : "Create Project"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik
        initialValues={{
          project_name: project?.project_name || "",
          client_name: project?.client_name || "",
          client_mobile: project?.client_mobile || "",
          address: project?.client_address || "",
          description: project?.description || "",
        }}
        validationSchema={projectSchema}
        onSubmit={handleSubmit}
      >
        <FormBody id={PROJECT_FORM_ID} saved={saved}>
          <Section title="Project details">
            <div className="fk-grid">
              <TextField name="project_name" label="Project Name" required placeholder="e.g. Villa 304 Interior" />
              <TextField name="client_name" label="Client Name" required placeholder="e.g. Rajesh Kumar" />
              <TextField name="client_mobile" label="Client Mobile" type="tel" placeholder="e.g. 9876543210" />
              <TextField name="address" label="Site Address / Location" placeholder="e.g. Sector 62, Noida" />
            </div>
          </Section>
          <Section title="Description">
            <TextAreaField
              name="description"
              label="Description"
              rows={3}
              placeholder="e.g. Full home renovation, modular kitchen, electricals"
            />
          </Section>
        </FormBody>
      </Formik>
    </FormPageLayout>
  );
};

/* ---------------- Add / edit a ledger entry ---------------- */

/** /partner/tools/projects/:projectId/ledger/new and /…/ledger/:ledgerId/edit */
export const ProjectLedgerFormPage = () => {
  const { projectId, ledgerId } = useParams();
  const isEdit = Boolean(ledgerId);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useToolsToken();
  const backUrl = projectUrl(projectId, "ledgers");
  const exit = useExitWizard(backUrl);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { item: entry, loading, notFound } = useEditItem({
    enabled: isEdit,
    handedOver: location.state?.ledger,
    find: async (t) => {
      const data = await getProjectLedgersList(t, projectId);
      return (data?.projectLedgers || []).find((l) => String(l.id) === String(ledgerId)) || null;
    },
  });

  const entryType = String(entry?.type || "").toUpperCase();
  const initialType = isEdit ? (entryType === "DEBIT" ? "DEBIT" : "CREDIT") : searchParams.get("type") === "DEBIT" ? "DEBIT" : "CREDIT";

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = {
        project_id: Number(projectId) || projectId,
        type: values.type,
        amount: Number(values.amount),
        transaction_date: toLedgerApiDate(values.date, entry?.transaction_date),
        description: values.remark,
      };
      assertSuccess(
        isEdit
          ? await editProjectLedgerEntry(token, { id: entry?.id ?? ledgerId, ...body })
          : await addProjectLedgerEntry(token, body),
        "Failed to save ledger entry"
      );
      showToast(isEdit ? "Ledger entry updated" : "Ledger entry added", { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(backUrl, { replace: true }), 0);
    } catch (err) {
      showToast(errorText(err, "Failed to save ledger entry"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This ledger entry could not be found. It may have been removed." onBack={() => navigate(backUrl)} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Ledger Entry" : "Add Project Ledger Entry"}
      subtitle={isEdit ? "Update this ledger entry." : "Record a payment received or an expense paid for this project."}
      formId={LEDGER_FORM_ID}
      submitLabel={isEdit ? "Update Entry" : "Save Entry"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik
        initialValues={{
          type: initialType,
          amount: entry?.amount ?? "",
          date: entry?.transaction_date ? ledgerDateInput(entry.transaction_date) : todayDate(),
          remark: entry?.description || "",
        }}
        validationSchema={ledgerSchema}
        onSubmit={handleSubmit}
      >
        <FormBody id={LEDGER_FORM_ID} saved={saved}>
          <Section title="Entry details">
            <div className="fk-grid">
              <SegmentField name="type" label="Entry Type" required options={LEDGER_TYPES} className="fk-col-full" />
              <CurrencyField name="amount" label="Amount" required placeholder="e.g. 25,000" />
              <TextField name="date" label="Date" required type="date" />
              <TextAreaField
                name="remark"
                label="Remark / Details"
                rows={3}
                placeholder="e.g. Client advance payment, Payment to carpenter, Tile purchase"
                className="fk-col-full"
              />
            </div>
          </Section>
        </FormBody>
      </Formik>
    </FormPageLayout>
  );
};

/* ---------------- Add / edit a note ---------------- */

/** /partner/tools/projects/:projectId/notes/new and /…/notes/:noteId/edit */
export const ProjectNoteFormPage = () => {
  const { projectId, noteId } = useParams();
  const isEdit = Boolean(noteId);
  const navigate = useNavigate();
  const location = useLocation();
  const token = useToolsToken();
  const backUrl = projectUrl(projectId, "notes");
  const exit = useExitWizard(backUrl);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { item: note, loading, notFound } = useEditItem({
    enabled: isEdit,
    handedOver: location.state?.note,
    find: async (t) => {
      const data = await getProjectNotes(t, projectId);
      return (data?.projectNotes || []).find((n) => String(n.id) === String(noteId)) || null;
    },
  });

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const body = { project_id: Number(projectId) || projectId, title: values.title, content: values.note };
      assertSuccess(
        isEdit
          ? await editProjectNote(token, { id: note?.id ?? noteId, ...body })
          : await addProjectNote(token, body),
        "Failed to save note"
      );
      showToast(isEdit ? "Project note updated" : "Project note added", { type: "success" });
      setSaved(true);
      allowLeaveOnce();
      setTimeout(() => navigate(backUrl, { replace: true }), 0);
    } catch (err) {
      showToast(errorText(err, "Failed to save note"), { type: "error" });
      setSubmitting(false);
    }
  };

  if (notFound) return <NotFound text="This note could not be found. It may have been removed." onBack={() => navigate(backUrl)} />;

  return (
    <FormPageLayout
      title={isEdit ? "Edit Note" : "Add Project Note"}
      subtitle={isEdit ? "Update this note." : "Keep meeting minutes, change requests and vendor commitments."}
      formId={NOTE_FORM_ID}
      submitLabel={isEdit ? "Update Note" : "Add Note"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik initialValues={{ title: note?.title || "", note: note?.content || "" }} validationSchema={noteSchema} onSubmit={handleSubmit}>
        <FormBody id={NOTE_FORM_ID} saved={saved}>
          <Section title="Note">
            <TextField name="title" label="Title" required placeholder="e.g. Kitchen tiles" />
            <TextAreaField
              name="note"
              label="Note Content"
              required
              rows={6}
              placeholder="e.g. Client requested changes in kitchen tiles, meeting minutes, vendor commitments"
            />
          </Section>
        </FormBody>
      </Formik>
    </FormPageLayout>
  );
};
